import * as React from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Stack,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  Autocomplete,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Tabs,
  Tab,
  Tooltip,
} from '@mui/material';
import { Assessment, Cancel, FileDownload, Refresh, Visibility } from '@mui/icons-material';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { useSelector } from 'react-redux';
import Navbar from '../setup/Navbar';
import Loading from '../../BuiltInComponent/Loading';
import '../../services/GlobalVariables';

const localDate = (date) => {
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 10);
};

const cancellationReason = (order) => {
  if (order.auditReason) return order.auditReason;
  const remarks = String(order.remarks || '');
  const marker = remarks.lastIndexOf('Cancelled:');
  if (marker >= 0) return remarks.slice(marker + 10).trim();
  if (remarks.includes('Cancelled from cancellation screen')) return 'Cancelled from cancellation screen';
  return remarks || '-';
};

export default function OrderCancellation() {
  const { user } = useSelector((state) => state.auth);

  const [orders, setOrders] = React.useState([]);
  const [filteredOrders, setFilteredOrders] = React.useState([]);
  const [search, setSearch] = React.useState('');
  const [orderType, setOrderType] = React.useState('all');
  const [reason, setReason] = React.useState('');
  const [openingDate, setOpeningDate] = React.useState('');
  const [orderDate, setOrderDate] = React.useState('');
  const [users, setUsers] = React.useState([]);
  const [selectedUserID, setSelectedUserID] = React.useState(user?.userID || '');
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [viewOrder, setViewOrder] = React.useState(null);
  const [activeTab, setActiveTab] = React.useState(0);
  const [reportOrders, setReportOrders] = React.useState([]);
  const [reportSummary, setReportSummary] = React.useState({});
  const [reportLoading, setReportLoading] = React.useState(false);
  const [reportError, setReportError] = React.useState('');
  const [reportSearch, setReportSearch] = React.useState('');
  const today = React.useMemo(() => new Date(), []);
  const [reportFromDate, setReportFromDate] = React.useState(localDate(new Date(today.getFullYear(), today.getMonth(), 1)));
  const [reportToDate, setReportToDate] = React.useState(localDate(today));
  const [reportUserID, setReportUserID] = React.useState('');
  const [reportOrderType, setReportOrderType] = React.useState('all');
  const selectedUser = users.find((item) => Number(item.userID) === Number(selectedUserID)) || user || null;

  React.useEffect(() => {
    if (!user?.userID) return;
    setSelectedUserID(user.userID);

    axios.post(`${global.SET_API_URL}users/getAllUsers`, {
      cmpID: user.cmpID,
      branchID: user.branchID || user.defaultBranchID || 1,
      includeAll: true,
    }).then((response) => {
      const activeUsers = Array.isArray(response.data) ? response.data : [];
      setUsers(activeUsers.filter(
        (item) => String(item?.userType || '').trim().toLowerCase() !== 'ordertaker'
      ));
    }).catch((err) => {
      setError(err.response?.data?.message || 'Unable to fetch users');
    });
  }, [user]);

  const loadOrders = React.useCallback(async () => {
    if (!user?.userID || !selectedUserID) return;

    setLoading(true);
    setError('');
    try {
      const response = await axios.get(
        `${global.SET_API_URL}posBusiness/getCancelableOrders/${selectedUserID}`,
        { params: { loggedInUserID: user.userID, orderDate: orderDate || undefined } }
      );

      if (response.data?.success) {
        const list = response.data.orders || [];
        setOrders(list);
        setFilteredOrders(list);
        const resolvedDate = String(response.data.openingDate || '').slice(0, 10);
        setOpeningDate(resolvedDate);
        if (!orderDate && resolvedDate) setOrderDate(resolvedDate);
      } else {
        setError(response.data?.message || 'Unable to fetch orders');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Unable to fetch orders');
    } finally {
      setLoading(false);
    }
  }, [selectedUserID, user?.userID, orderDate]);

  React.useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  React.useEffect(() => {
    const q = search.trim().toLowerCase();
    const filtered = orders.filter((order) => {
      const orderId = String(order.orderID || '').toLowerCase();
      const table = String(order.tableLabel || '').toLowerCase();
      const customer = String(order.customerName || '').toLowerCase();
      const type = String(order.orderType || '').toLowerCase();
      const aggregator = String(order.aggregatorPlatform || '').toLowerCase();
      const matchesType = orderType === 'all'
        || (orderType === 'foodpanda' ? aggregator === 'foodpanda' : type === orderType && aggregator !== 'foodpanda');
      const matchesSearch = !q || orderId.includes(q) || table.includes(q) || customer.includes(q) || type.includes(q) || aggregator.includes(q);
      return matchesType && matchesSearch;
    });

    setFilteredOrders(filtered);
  }, [search, orderType, orders]);

  const handleCancelOrder = async (orderID) => {
    if (!orderID) return;

    const ok = window.confirm(`Are you sure you want to cancel order #${orderID}?`);
    if (!ok) return;

    if (!user?.userID) {
      alert('Logged-in user not found.');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${global.SET_API_URL}posBusiness/cancelOrder`, {
        orderID,
        userID: selectedUserID,
        loggedInUserID: user.userID,
        reason: reason || null,
      });

      if (response.data?.success) {
        alert(response.data.message || 'Order cancelled successfully');
        await loadOrders();
      } else {
        alert(response.data?.message || 'Order cancellation failed');
      }
    } catch (err) {
      alert(err.response?.data?.message || err.message || 'Order cancellation failed');
    } finally {
      setLoading(false);
    }
  };

  const loadCancellationReport = React.useCallback(async () => {
    if (!user?.userID) return;
    setReportLoading(true);
    setReportError('');
    try {
      const response = await axios.get(`${global.SET_API_URL}posBusiness/cancellationReport`, {
        params: {
          loggedInUserID: user.userID,
          fromDate: reportFromDate || undefined,
          toDate: reportToDate || undefined,
          userID: reportUserID || undefined,
          orderType: reportOrderType,
        },
      });
      if (response.data?.success) {
        const positiveOrders = (response.data.orders || []).filter(
          (order) => Number(order.grandTotal || 0) > 0
        );
        const summary = positiveOrders.reduce((result, order) => {
          const amount = Number(order.grandTotal || 0);
          result.totalOrders += 1;
          result.totalAmount += amount;
          result.totalItems += Number(order.totalQuantity || 0);
          result.highestOrder = Math.max(result.highestOrder, amount);
          if (order.cancelledByUserID) result.auditedOrders += 1;
          return result;
        }, { totalOrders: 0, totalAmount: 0, totalItems: 0, highestOrder: 0, auditedOrders: 0 });
        summary.averageOrder = summary.totalOrders ? summary.totalAmount / summary.totalOrders : 0;
        summary.legacyOrders = summary.totalOrders - summary.auditedOrders;
        setReportOrders(positiveOrders);
        setReportSummary(summary);
      } else setReportError(response.data?.message || 'Unable to load cancellation report');
    } catch (err) {
      setReportError(err.response?.data?.message || err.message || 'Unable to load cancellation report');
    } finally {
      setReportLoading(false);
    }
  }, [user?.userID, reportFromDate, reportToDate, reportUserID, reportOrderType]);

  React.useEffect(() => {
    if (activeTab === 1) loadCancellationReport();
  }, [activeTab, loadCancellationReport]);

  const visibleReportOrders = React.useMemo(() => {
    const q = reportSearch.trim().toLowerCase();
    if (!q) return reportOrders;
    return reportOrders.filter((order) => [
      order.orderID, order.orderOwnerName, order.orderOwnerUserName, order.cancelledByName,
      order.cancelledByUserName, order.customerName, order.tableLabel,
      order.orderType, order.aggregatorPlatform, cancellationReason(order),
    ].some((value) => String(value || '').toLowerCase().includes(q)));
  }, [reportOrders, reportSearch]);

  const reportBreakdown = React.useMemo(() => {
    const result = {};
    visibleReportOrders.forEach((order) => {
      const key = String(order.aggregatorPlatform || '').toLowerCase() === 'foodpanda'
        ? 'Foodpanda' : (order.orderType || 'Unknown');
      if (!result[key]) result[key] = { count: 0, amount: 0 };
      result[key].count += 1;
      result[key].amount += Number(order.grandTotal || 0);
    });
    return Object.entries(result).sort((a, b) => b[1].amount - a[1].amount);
  }, [visibleReportOrders]);

  const exportReport = () => {
    const rows = visibleReportOrders.map((order) => ({
      'Order #': order.orderID,
      'Order Date': order.entDate ? String(order.entDate).slice(0, 10) : '',
      'Cancelled At': order.cancelledAt ? new Date(order.cancelledAt).toLocaleString() : '',
      'Order User': order.orderOwnerName || order.orderOwnerUserName || `User #${order.userID}`,
      'Cancelled By': order.cancelledByName || order.cancelledByUserName || 'Legacy / unavailable',
      Type: String(order.aggregatorPlatform || '').toLowerCase() === 'foodpanda' ? 'Foodpanda' : order.orderType,
      Table: order.tableLabel || '', Customer: order.customerName || '',
      Items: Number(order.totalQuantity || 0), 'Grand Total': Number(order.grandTotal || 0),
      Reason: cancellationReason(order), 'Audit Source': order.cancelledByUserID ? 'Audit history' : 'Legacy fallback',
    }));
    const sheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, 'Cancellation Report');
    XLSX.writeFile(workbook, `Cancellation_Report_${reportFromDate || 'all'}_${reportToDate || 'all'}.xlsx`);
  };

  return (
    <>
      <Navbar />
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Typography variant="h5" fontWeight={700} gutterBottom>
          Order Cancellation
        </Typography>

        <Paper variant="outlined" sx={{ mb: 2 }}>
          <Tabs value={activeTab} onChange={(event, value) => setActiveTab(value)}>
            <Tab icon={<Cancel />} iconPosition="start" label="Order Cancellation" />
            <Tab icon={<Assessment />} iconPosition="start" label="Cancellation Report" />
          </Tabs>
        </Paper>

        {activeTab === 0 ? <>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Opening Date: {openingDate || '-'} | User: {selectedUser?.userName || selectedUser?.dispName || selectedUser?.userID || '-'}
        </Typography>

        <Paper sx={{ p: 2, mb: 2 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
              <Autocomplete
                fullWidth
                size="small"
                options={users}
                value={selectedUser}
                getOptionLabel={(option) => option?.dispName || option?.userName || `User #${option?.userID || ''}`}
                isOptionEqualToValue={(option, value) => Number(option?.userID) === Number(value?.userID)}
                onChange={(event, value) => setSelectedUserID(value?.userID || '')}
                renderInput={(params) => <TextField {...params} label="Select User" />}
              />
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Order Date"
              value={orderDate || openingDate}
              onChange={(e) => setOrderDate(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <TextField
              fullWidth
              size="small"
              label="Search (Order ID / Table / Customer / Type)"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <TextField
              select
              fullWidth
              size="small"
              label="Order Type"
              value={orderType}
              onChange={(e) => setOrderType(e.target.value)}
            >
              <MenuItem value="all">All Order Types</MenuItem>
              <MenuItem value="dinein">Dine In</MenuItem>
              <MenuItem value="takeaway">Takeaway</MenuItem>
              <MenuItem value="dispatch">Dispatch</MenuItem>
              <MenuItem value="delivery">Delivery</MenuItem>
              <MenuItem value="foodpanda">Foodpanda</MenuItem>
            </TextField>
            <TextField
              fullWidth
              size="small"
              label="Cancellation Reason (Optional)"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
            <Button variant="outlined" startIcon={<Refresh />} onClick={loadOrders} fullWidth>
              Refresh
            </Button>
          </Stack>
        </Paper>

        {loading && (
          <Box sx={{ mb: 2 }}>
            <Loading loadingText="Loading orders" />
          </Box>
        )}

        {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Order #</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Table</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell align="right">Total</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Time</TableCell>
                <TableCell align="center">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredOrders.length > 0 ? (
                filteredOrders.map((order) => (
                  <TableRow key={order.orderID} hover>
                    <TableCell>{order.orderID}</TableCell>
                    <TableCell>{String(order.aggregatorPlatform || '').toLowerCase() === 'foodpanda' ? 'Foodpanda' : order.orderType}</TableCell>
                    <TableCell>{order.tableLabel || '-'}</TableCell>
                    <TableCell>{order.customerName || '-'}</TableCell>
                    <TableCell align="right">{Number(order.grandTotal || 0).toLocaleString()}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={order.orderStatus || '-'}
                        color={String(order.orderStatus || '').toLowerCase() === 'completed' ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      {order.createdAt ? new Date(order.createdAt).toLocaleString() : '-'}
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="center">
                        <Button size="small" variant="outlined" startIcon={<Visibility />} onClick={() => setViewOrder(order)}>
                          View Order
                        </Button>
                        <Button size="small" variant="contained" color="error" startIcon={<Cancel />} onClick={() => handleCancelOrder(order.orderID)}>
                          Cancel Order
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    No orders found for cancellation
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        </> : <CancellationReport
          users={users} orders={visibleReportOrders} summary={reportSummary} breakdown={reportBreakdown}
          loading={reportLoading} error={reportError} fromDate={reportFromDate} toDate={reportToDate}
          userID={reportUserID} orderType={reportOrderType} search={reportSearch}
          setFromDate={setReportFromDate} setToDate={setReportToDate} setUserID={setReportUserID}
          setOrderType={setReportOrderType} setSearch={setReportSearch} onLoad={loadCancellationReport}
          onExport={exportReport}
        />}
      </Container>
      <Dialog open={Boolean(viewOrder)} onClose={() => setViewOrder(null)} maxWidth="md" fullWidth>
        <DialogTitle>Order #{viewOrder?.orderID || ''} Details</DialogTitle>
        <DialogContent dividers>
          {viewOrder && <>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} divider={<Divider orientation="vertical" flexItem />} sx={{ mb: 2 }}>
              <Box><Typography variant="caption" color="text.secondary">Type</Typography><Typography>{String(viewOrder.aggregatorPlatform || '').toLowerCase() === 'foodpanda' ? 'Foodpanda' : viewOrder.orderType || '-'}</Typography></Box>
              <Box><Typography variant="caption" color="text.secondary">Customer</Typography><Typography>{viewOrder.customerName || '-'}</Typography></Box>
              <Box><Typography variant="caption" color="text.secondary">Phone</Typography><Typography>{viewOrder.customerPhone || '-'}</Typography></Box>
              <Box><Typography variant="caption" color="text.secondary">Table</Typography><Typography>{viewOrder.tableLabel || '-'}</Typography></Box>
              <Box><Typography variant="caption" color="text.secondary">Date / Time</Typography><Typography>{viewOrder.createdAt ? new Date(viewOrder.createdAt).toLocaleString() : '-'}</Typography></Box>
            </Stack>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead><TableRow><TableCell>Item</TableCell><TableCell align="right">Qty</TableCell><TableCell align="right">Rate</TableCell><TableCell align="right">Discount</TableCell><TableCell align="right">Amount</TableCell></TableRow></TableHead>
                <TableBody>
                  {(viewOrder.items || []).map((item) => <TableRow key={item.orderItemID}>
                    <TableCell><Typography variant="body2" fontWeight={600}>{item.itemName || '-'}</Typography>{item.notes && <Typography variant="caption" color="text.secondary">{item.notes}</Typography>}</TableCell>
                    <TableCell align="right">{Number(item.quantity || 0).toLocaleString()}</TableCell><TableCell align="right">{Number(item.unitPrice || 0).toLocaleString()}</TableCell><TableCell align="right">{Number(item.lineDiscount || 0).toLocaleString()}</TableCell><TableCell align="right">{Number(item.itemTotal || 0).toLocaleString()}</TableCell>
                  </TableRow>)}
                  {(viewOrder.items || []).length === 0 && <TableRow><TableCell colSpan={5} align="center">No item details found.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </TableContainer>
            <Stack alignItems="flex-end" spacing={0.5} sx={{ mt: 2 }}>
              <Typography>Subtotal: Rs. {Number(viewOrder.subtotal || 0).toLocaleString()}</Typography>
              {Number(viewOrder.overallDiscount || 0) > 0 && <Typography>Overall Discount: - Rs. {Number(viewOrder.overallDiscount).toLocaleString()}</Typography>}
              {Number(viewOrder.couponAmount || 0) > 0 && <Typography>Coupon Discount: - Rs. {Number(viewOrder.couponAmount).toLocaleString()}</Typography>}
              <Typography variant="h6" fontWeight={800}>Grand Total: Rs. {Number(viewOrder.grandTotal || 0).toLocaleString()}</Typography>
            </Stack>
          </>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewOrder(null)}>Close</Button>
          <Button color="error" variant="contained" startIcon={<Cancel />} onClick={() => { const id = viewOrder?.orderID; setViewOrder(null); handleCancelOrder(id); }}>Cancel This Order</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}


function CancellationReport({
  users, orders, summary, breakdown, loading, error, fromDate, toDate, userID, orderType, search,
  setFromDate, setToDate, setUserID, setOrderType, setSearch, onLoad, onExport,
}) {
  const selectedUser = users.find((item) => Number(item.userID) === Number(userID)) || null;
  const cards = [
    ['Cancelled Orders', summary.totalOrders || 0],
    ['Cancelled Amount', `Rs. ${Number(summary.totalAmount || 0).toLocaleString()}`],
    ['Average Order', `Rs. ${Number(summary.averageOrder || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`],
    ['Highest Order', `Rs. ${Number(summary.highestOrder || 0).toLocaleString()}`],
    ['Cancelled Qty', Number(summary.totalItems || 0).toLocaleString()],
  ];

  return <>
    <Paper sx={{ p: 2, mb: 2 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
        <TextField fullWidth size="small" type="date" label="From Date" value={fromDate}
          onChange={(e) => setFromDate(e.target.value)} slotProps={{ inputLabel: { shrink: true } }} />
        <TextField fullWidth size="small" type="date" label="To Date" value={toDate}
          onChange={(e) => setToDate(e.target.value)} slotProps={{ inputLabel: { shrink: true } }} />
        <Autocomplete fullWidth size="small" options={users} value={selectedUser}
          getOptionLabel={(option) => option?.dispName || option?.userName || `User #${option?.userID || ''}`}
          isOptionEqualToValue={(option, value) => Number(option?.userID) === Number(value?.userID)}
          onChange={(event, value) => setUserID(value?.userID || '')}
          renderInput={(params) => <TextField {...params} label="Order User (All)" />} />
        <TextField select fullWidth size="small" label="Order Type" value={orderType} onChange={(e) => setOrderType(e.target.value)}>
          <MenuItem value="all">All Order Types</MenuItem><MenuItem value="dinein">Dine In</MenuItem>
          <MenuItem value="takeaway">Takeaway</MenuItem><MenuItem value="dispatch">Dispatch</MenuItem>
          <MenuItem value="delivery">Delivery</MenuItem><MenuItem value="foodpanda">Foodpanda</MenuItem>
        </TextField>
        <Button variant="contained" startIcon={<Refresh />} onClick={onLoad} disabled={loading} fullWidth>Generate</Button>
      </Stack>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mt: 1.5 }}>
        <TextField fullWidth size="small" label="Search order, user, customer, table or reason" value={search} onChange={(e) => setSearch(e.target.value)} />
        <Button variant="outlined" color="success" startIcon={<FileDownload />} onClick={onExport}
          disabled={!orders.length} sx={{ minWidth: 190 }}>Export Excel</Button>
      </Stack>
    </Paper>

    {loading && <Box sx={{ mb: 2 }}><Loading loadingText="Generating cancellation report" /></Box>}
    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
    {Number(summary.legacyOrders || 0) > 0 && <Alert severity="info" sx={{ mb: 2 }}>
      {summary.legacyOrders} older cancellation(s) have no exact canceller audit. Their cancellation time is estimated from the last order update.
    </Alert>}

    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(5, 1fr)' }, gap: 1.5, mb: 2 }}>
      {cards.map(([label, value]) => <Paper key={label} variant="outlined" sx={{ p: 2, borderTop: '4px solid', borderTopColor: 'error.main' }}>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="h6" fontWeight={800}>{value}</Typography>
      </Paper>)}
    </Box>

    {breakdown.length > 0 && <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Typography fontWeight={700} sx={{ mb: 1 }}>Order Type Breakdown</Typography>
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
        {breakdown.map(([type, data]) => <Chip key={type}
          label={`${type}: ${data.count} order(s) / Rs. ${data.amount.toLocaleString()}`} variant="outlined" color="error" />)}
      </Stack>
    </Paper>}

    <TableContainer component={Paper}>
      <Table size="small" stickyHeader>
        <TableHead><TableRow>
          <TableCell>Order / Type</TableCell><TableCell>Cancellation Time</TableCell>
          <TableCell>Order User</TableCell><TableCell>Cancelled By</TableCell>
          <TableCell>Customer / Table</TableCell><TableCell align="right">Qty</TableCell>
          <TableCell align="right">Amount</TableCell><TableCell>Reason</TableCell>
        </TableRow></TableHead>
        <TableBody>
          {orders.map((order) => <TableRow key={order.orderID} hover>
            <TableCell><Typography variant="body2" fontWeight={700}>#{order.orderID}</Typography>
              <Chip size="small" label={String(order.aggregatorPlatform || '').toLowerCase() === 'foodpanda' ? 'Foodpanda' : order.orderType || '-'} /></TableCell>
            <TableCell><Typography variant="body2">{order.cancelledAt ? new Date(order.cancelledAt).toLocaleString() : '-'}</Typography>
              <Typography variant="caption" color="text.secondary">Order: {order.createdAt ? new Date(order.createdAt).toLocaleString() : '-'}</Typography></TableCell>
            <TableCell>{order.orderOwnerName || order.orderOwnerUserName || `User #${order.userID}`}</TableCell>
            <TableCell>{order.cancelledByName || order.cancelledByUserName || <Tooltip title="Older cancellation: exact user was not recorded"><Chip size="small" label="Legacy / unavailable" color="warning" /></Tooltip>}</TableCell>
            <TableCell><Typography variant="body2">{order.customerName || '-'}</Typography><Typography variant="caption" color="text.secondary">Table: {order.tableLabel || '-'}</Typography></TableCell>
            <TableCell align="right">{Number(order.totalQuantity || 0).toLocaleString()}<Typography variant="caption" display="block" color="text.secondary">{order.itemCount || 0} lines</Typography></TableCell>
            <TableCell align="right"><Typography fontWeight={700}>Rs. {Number(order.grandTotal || 0).toLocaleString()}</Typography></TableCell>
            <TableCell sx={{ minWidth: 180, maxWidth: 280 }}><Tooltip title={cancellationReason(order)}><Typography variant="body2" noWrap>{cancellationReason(order)}</Typography></Tooltip></TableCell>
          </TableRow>)}
          {!orders.length && !loading && <TableRow><TableCell colSpan={8} align="center" sx={{ py: 5 }}>No cancelled orders found for the selected filters.</TableCell></TableRow>}
        </TableBody>
      </Table>
    </TableContainer>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
      Showing {orders.length} record(s). Report is restricted to the logged-in user's company and branch.
    </Typography>
  </>;
}
